import { ArrowLeft, ClipboardCheck } from 'lucide-react'
import { CategoryAchievementChart } from '~/components/assessment-detail/CategoryAchievementChart'
import { ScoreDrilldownTable } from '~/components/assessment-detail/ScoreDrilldownTable'
import { SummaryHeader } from '~/components/assessment-detail/SummaryHeader'
import { useAssessmentDetail } from '~/components/assessment-detail/useAssessmentDetail'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'

// 인증평가 상세 페이지(/assessments/:id). 헤더 + 분석 StatCard + 항목별 점수 드릴다운 +
// 대분류별 달성률 + 보고서 요약. 상담 SessionDetailPage 와 동일한 상세 패턴.
// 데이터 로드·상태·부수효과는 useAssessmentDetail 훅으로, 표시는 하위 컴포넌트로 분리하고
// 이 컴포넌트는 오케스트레이션 + 레이아웃(뒤로가기/스켈레톤/빈 상태 + 확인 모달)만 담당한다.
export function AssessmentDetailPage() {
    const {
        navigate,
        detail,
        loading,
        busy,
        rescoreConfirm,
        setRescoreConfirm,
        deleteConfirm,
        setDeleteConfirm,
        isSuperadmin,
        canManage,
        canScoreOps,
        handleRegenerate,
        handleRescore,
        handleDelete,
        ratio,
        pct,
        sortedScores,
        categoryData,
    } = useAssessmentDetail()

    return (
        <div className='space-y-5'>
            <div className='flex items-center gap-2'>
                <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => navigate(-1)}
                    aria-label='뒤로가기'>
                    <ArrowLeft />
                    뒤로
                </Button>
            </div>

            {loading ? (
                <DetailSkeleton />
            ) : !detail ? (
                <EmptyState
                    icon={ClipboardCheck}
                    title='평가를 찾을 수 없어요.'
                    description='삭제되었거나 접근 권한이 없는 평가예요.'
                    action={
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => navigate(-1)}>
                            뒤로가기
                        </Button>
                    }
                />
            ) : (
                <>
                    <SummaryHeader
                        detail={detail}
                        canManage={canManage}
                        isSuperadmin={isSuperadmin}
                        canScoreOps={canScoreOps}
                        busy={busy}
                        ratio={ratio}
                        pct={pct}
                        handleRegenerate={handleRegenerate}
                        setRescoreConfirm={setRescoreConfirm}
                        setDeleteConfirm={setDeleteConfirm}
                    />

                    {categoryData.length > 0 ? (
                        <CategoryAchievementChart data={categoryData} />
                    ) : null}

                    <ScoreDrilldownTable sortedScores={sortedScores} />

                    {detail.reportSummary ? (
                        <Card className='p-5'>
                            <h3 className='krds-title text-foreground mb-2'>
                                보고서 요약
                            </h3>
                            <p className='text-foreground text-[13px] whitespace-pre-wrap'>
                                {detail.reportSummary}
                            </p>
                        </Card>
                    ) : null}

                    <ConfirmDialog
                        open={rescoreConfirm}
                        title='재채점'
                        description='점수가 다시 계산됩니다. 수십 초 걸릴 수 있어요. 진행할까요?'
                        confirmLabel='재채점'
                        onConfirm={() => {
                            setRescoreConfirm(false)
                            handleRescore()
                        }}
                        onCancel={() => setRescoreConfirm(false)}
                    />
                    <ConfirmDialog
                        open={deleteConfirm}
                        title='평가 삭제'
                        description='평가를 영구 삭제합니다. 되돌릴 수 없어요.'
                        confirmLabel='삭제'
                        danger
                        onConfirm={() => {
                            setDeleteConfirm(false)
                            handleDelete()
                        }}
                        onCancel={() => setDeleteConfirm(false)}
                    />
                </>
            )}
        </div>
    )
}

function DetailSkeleton() {
    return (
        <div className='space-y-5'>
            <Skeleton className='h-7 w-64' />
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-5'>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className='h-20' />
                ))}
            </div>
            <Skeleton className='h-56' />
            <Skeleton className='h-64' />
        </div>
    )
}
